import http from './http';

// GET /api/suadmin/funnel?from=&to=&segment= (auth admin) → AnalyticsFunnelResponse
// (contrat C4 **v2**, B6_01/B6_03 — 6 étapes, `retention[]`, `conversions`, `cohort`).
// http.js préfixe déjà /api et ajoute le header Authorization (gc_token). Signature réelle de
// http.get = un seul argument `path` (pas d'objet { params }) → query string construite à la
// main : un `segment` passé autrement serait silencieusement ignoré.
// Calque dashboardService.js / blocsAffichesService.js.

// Valeurs de segment servies par l'API (`funnel_controller.SEGMENTS`). ⛔ `non_wp` n'existe
// PAS (D24) : rien en base ne distingue « site créé au back-office » de « plugin installé sans
// jamais se connecter ». Une valeur inconnue est normalisée en `all` côté serveur — on ne
// s'appuie pas sur ce repli, on n'émet que des valeurs de cette liste.
const SEGMENTS = ['all', 'wp', 'unknown'];

// ⛔ GARDE « HTTP 200 + { gcCode: 410 } » = session expirée, PAS une donnée (décision L8,
// tranchée en fenêtre le 2026-09-02). routes.js pose `suadmin.use(auth.ensureAuthenticated)`
// AVANT `ensureAdmin`, et ce middleware répond 200 + gcCode sur un JWT absent/expiré ; http.js
// ne lève que sur `!res.ok`. Sans cette garde, l'écran affichait « Aucune donnée sur la période
// sélectionnée » sur une session morte — un vide de données là où il n'y a qu'une absence de
// droit de lire. C'est la même faute que le « 0,0 % » posé sur une étape non mesurée, que cet
// écran existe précisément pour supprimer. `withAuth` ne revalide qu'AU MONTAGE : un onglet
// laissé ouvert puis « Rafraîchir » tombe exactement dans ce cas.
// Pattern : blocsAffichesService.rejectGcCode (lui-même allégé de moderationDashboardService).
const SESSION_EXPIRED = 'session_expired';

function rejectGcCode(result) {
  // Un corps NON JSON (page d'erreur d'un proxy, réponse vide) n'est pas une session expirée :
  // http.js rend alors du texte — on le dit tel quel plutôt que d'envoyer se reconnecter.
  if (result === undefined || result === null || typeof result !== 'object') {
    const error = new Error('réponse inattendue du serveur (corps non JSON).');
    error.code = 'unexpected_response';
    throw error;
  }
  const hasErr = result.err && result.err !== 'undefined';
  if (result.gcCode || hasErr) {
    const error = new Error(
      'session expirée ou accès refusé. Recharge la page pour te reconnecter.'
    );
    error.code = SESSION_EXPIRED;
    error.body = result;
    throw error;
  }
  return result;
}

// `segment` omis ou `'all'` → paramètre NON transmis : l'API a déjà `all` par défaut, et une
// query string plus courte reste lisible dans l'onglet réseau pendant une recette.
const getFunnel = (from, to, segment) => {
  const params = [];
  if (from) params.push(`from=${encodeURIComponent(from)}`);
  if (to) params.push(`to=${encodeURIComponent(to)}`);
  if (segment && segment !== 'all' && SEGMENTS.includes(segment)) {
    params.push(`segment=${encodeURIComponent(segment)}`);
  }
  const qs = params.length ? `?${params.join('&')}` : '';
  return http.get(`/suadmin/funnel${qs}`).then(rejectGcCode);
};

// GET /api/suadmin/funnel/timeseries?from=&to=&segment= (auth admin) → réponse C7 (B6_04) :
// { from, to, segment, days[], series.{inscriptions,plugin_connect,activations,deactivations},
//   version_marks[] } — séries en TABLEAUX indexés sur `days`, comptées en sites (D27).
// Même patron que `getFunnel`, et le patron a DEUX faces (ticket B8_02) : la query string à la
// main (http.get ne prend qu'un path — un `segment` passé autrement partirait à la poubelle en
// silence) ET la garde `rejectGcCode` (L8) — sans elle, un `200 + {gcCode:410}` passerait pour
// une réponse valide → `days`/`series` undefined → graphe vide silencieux, juste sous l'alerte
// « session expirée » du parent.
const getFunnelTimeseries = (from, to, segment) => {
  const params = [];
  if (from) params.push(`from=${encodeURIComponent(from)}`);
  if (to) params.push(`to=${encodeURIComponent(to)}`);
  if (segment && segment !== 'all' && SEGMENTS.includes(segment)) {
    params.push(`segment=${encodeURIComponent(segment)}`);
  }
  const qs = params.length ? `?${params.join('&')}` : '';
  return http.get(`/suadmin/funnel/timeseries${qs}`).then(rejectGcCode);
};

export default { getFunnel, getFunnelTimeseries, SEGMENTS, SESSION_EXPIRED };
export { getFunnel, getFunnelTimeseries, SEGMENTS, SESSION_EXPIRED };
